import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useParams } from '@/lib/router'

import { fetchVisitPhoto } from './api'

/**
 * Where the Visit MIS's photo link lands — from the screen or from a cell in
 * the downloaded Excel. A page rather than the API link itself: a link opened
 * from a spreadsheet cannot carry the sign-in, this signed-in page can. The
 * server shows the photo only for a visit inside the viewer's reports.
 */
export function VisitPhotoPage() {
  const { visitId } = useParams({ from: '/reports/visit-photo/$visitId' })
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!visitId) return
    let url: string | null = null
    let cancelled = false
    fetchVisitPhoto(visitId)
      .then((u) => {
        url = u
        if (!cancelled) setSrc(u)
      })
      .catch((err) => {
        if (cancelled) return
        const status = isAxiosError(err) ? err.response?.status : undefined
        setError(
          status === 404
            ? 'There is no photo for this visit, or the visit is outside the records your role covers.'
            : status === 403
              ? 'You do not have access to the Visit MIS.'
              : 'The photo could not be loaded. Try again in a moment.',
        )
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [visitId])

  return (
    <div className="p-6">
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Visit proof photo</CardTitle>
          <p className="font-mono text-xs text-slate-500">{visitId}</p>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {!error && !src && <SkeletonRows rows={6} />}
          {src && <img src={src} alt="Photo taken at the visit" className="max-h-[70vh] rounded-md border border-slate-200" />}
        </CardContent>
      </Card>
    </div>
  )
}
